import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Currency } from "@/money/currency_constants";

/** Mirrors SolderaBankAccountDetails in app/money/invoice/invoice_validators.py. */
export interface SolderaBankAccountDetails {
  /** null for US details, which a payer reaches by account and routing number */
  iban: string | null;
  swift_bic: string;
  bank_name_and_address: string;
  account_number: string | null;
  sort_code: string | null;
  routing_number: string | null;
  account_type: string | null;
}

/**
 * Mirrors SolderaBankAccountResponse: the account a buyer invoice in `currency` names.
 *
 * @property bank_account - null when Soldera holds no account in `currency`; the backend refuses such an invoice
 */
export interface SolderaBankAccountResponse {
  currency: Currency;
  bank_account: SolderaBankAccountDetails | null;
}

/**
 * Mirrors GenerateBuyerSalesInvoiceRequest in app/money/invoice/invoice_validators.py.
 *
 * @property company_id - Soldera's own company for a buyer outside the platform
 * @property sales_transaction_id - the deal the invoice bills, which its VAT is calculated from; the API refuses null, which the modal holds only while no deal is selected
 * @property fee_items - extra fee lines, each on the invoice's MWh amount and VAT
 */
export interface BuyerSalesInvoiceCreateRequest {
  company_id: number;
  description: string;
  amount: number;
  unit_price: number;
  reference: string | null;
  buyer_name: string;
  buyer_address: string;
  buyer_country: string;
  buyer_registry_code: string | null;
  buyer_vat: string | null;
  buyer_emails: string | null;
  fee_items: { item_type: string; description: string; unit_price: number }[];
  sales_transaction_id: number | null;
}

/**
 * The invoice `POST /api/admin/buyer-sales-invoice` created.
 *
 * @property invoice - absent when the backend answers without the created invoice
 */
export interface BuyerSalesInvoiceCreateResponse {
  invoice?: { id: number };
}

export const invoiceApi = {
  /**
   * Fetches the Soldera bank account a buyer invoice in `currency` names
   * (`GET /api/admin/soldera-bank-accounts/<currency>`).
   *
   * @throws Error if the request fails; a currency Soldera holds no account in is not a failure
   */
  getSolderaBankAccount: async (
    currency: Currency
  ): Promise<SolderaBankAccountResponse> => {
    const response = await goSolidApi.get<SolderaBankAccountResponse>(
      `/api/admin/soldera-bank-accounts/${currency}`
    );
    return response.data;
  },

  /**
   * Creates a sales invoice to a buyer (`POST /api/admin/buyer-sales-invoice`).
   *
   * @throws Error if the request fails, including when the backend refuses the invoice
   */
  createBuyerSalesInvoice: async (
    payload: BuyerSalesInvoiceCreateRequest
  ): Promise<BuyerSalesInvoiceCreateResponse> => {
    const response = await goSolidApi.post<BuyerSalesInvoiceCreateResponse>(
      "/api/admin/buyer-sales-invoice",
      payload
    );
    return response.data;
  },
};
